import { Typography, Table, Tag, Button, Input } from 'antd';
import { SearchOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DEBOUNCE_DELAY } from '@/constants';

const PAGE_SIZE = 20;

function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
}

interface AuditLog { id: string; action: string; entityType: string; ipAddress: string | null; createdAt: string; user: { name: string; role: string } }
interface ApiResult { data: { data: AuditLog[]; meta: { cursor: string; hasMore: boolean; total: number } } }

export default function AuditLogsPage() {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const page = cursorHistory.length + 1;
  const isMobile = useMediaQuery('(max-width: 768px)');

  const debouncedSearch = useMemo(
    () => debounce((value: string) => { setSearch(value); setCursor(undefined); setCursorHistory([]); }, DEBOUNCE_DELAY),
    [],
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', search, cursor],
    queryFn: () => api.get<unknown, ApiResult>('/admin/audit-logs', {
      params: { search: search || undefined, cursor, limit: PAGE_SIZE },
    }),
  });

  const logs: AuditLog[] = data?.data?.data ?? [];
  const meta = data?.data?.meta;
  const total = meta?.total ?? 0;

  function handleNext() { setCursorHistory((h) => [...h, cursor]); setCursor(meta?.cursor); }
  function handlePrev() { const h = [...cursorHistory]; const prev = h.pop(); setCursorHistory(h); setCursor(prev); }

  const columns = [
    {
      title: 'Time', dataIndex: 'createdAt', key: 'createdAt', width: 180,
      render: (d: string) => <span style={{ color: '#64748b', fontSize: 12 }}>{new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>,
    },
    {
      title: 'User', key: 'user', width: 200,
      render: (_: unknown, r: AuditLog) => (
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.user?.name ?? '—'} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({r.user?.role ?? ''})</span></span>
      ),
    },
    {
      title: 'Action', dataIndex: 'action', key: 'action',
      render: (a: string) => <Tag style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}>{a}</Tag>,
    },
    { title: 'Entity', dataIndex: 'entityType', key: 'entityType', render: (v: string) => <span style={{ color: '#64748b' }}>{v}</span> },
    { title: 'IP Address', dataIndex: 'ipAddress', key: 'ipAddress', render: (ip: string | null) => <span style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 12 }}>{ip ?? '—'}</span> },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Audit Logs</Typography.Title>
        <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>System audit trail of admin actions</Typography.Text>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search by user name…"
          onChange={handleSearchChange}
          style={{ flex: '1 1 180px', maxWidth: 360, height: 32 }}
          allowClear
        />
      </div>

      {isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : logs.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No audit logs found</div>
          ) : logs.map((r) => (
            <div key={r.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <div>
                  <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{r.user?.name ?? '—'}</span>
                  <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 6 }}>({r.user?.role ?? ''})</span>
                </div>
                <Tag style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, margin: 0, flexShrink: 0 }}>{r.action}</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                <span style={{ color: '#64748b', fontSize: 13 }}>{r.entityType}</span>
                <span style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 12 }}>{r.ipAddress ?? '—'}</span>
              </div>
              <div style={{ marginTop: 6, color: '#94a3b8', fontSize: 12 }}>
                {new Date(r.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
          size="small"
        />
      )}

      <div className="cw-pagination">
        <span className="cw-page-info">
          Page {page}{total > 0 && <> &middot; {total.toLocaleString()} record{total !== 1 ? 's' : ''}</>}
        </span>
        <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={cursorHistory.length === 0}>Previous</Button>
        <Button icon={<RightOutlined />} onClick={handleNext} disabled={!meta?.hasMore}>Next</Button>
      </div>
    </div>
  );
}
